import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  const apply = (data) => {
    setUser(data?.user ?? null);
    setWorkspace(data?.workspace ?? null);
    return data;
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => apply(res.data))
      .catch(() => apply(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    return apply(res.data);
  }, []);

  const signup = useCallback(async (values) => {
    const res = await api.post("/auth/signup", values);
    return apply(res.data);
  }, []);

  const updateProfile = useCallback(async (values) => {
    const res = await api.patch("/auth/me", values);
    return apply(res.data);
  }, []);

  const logout = useCallback(async () => {
    const wasClient = user?.role === "client";
    try {
      await api.post("/auth/logout");
    } finally {
      // Full page load on purpose: wipes every bit of in-memory app state.
      window.location.replace(wasClient ? "/client" : "/login");
    }
  }, [user]);

  const value = useMemo(
    () => ({ user, workspace, loading, login, signup, logout, updateProfile }),
    [user, workspace, loading, login, signup, logout, updateProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
